import { useEffect, useState } from "react";
import type { Discrepancy } from "../types";
import "./PanelDiscrepancies.css";

type DiscrepancyRowProps = {
  d: Discrepancy;
};

function DiscrepancyRow({ d }: DiscrepancyRowProps) {
  const isUnverifiable = d.type === "v1_envelope_unverifiable";
  return (
    <div className="drow" data-completion-id={d.completion_section_id}>
      <div className="drow-main">
        <span className="drow-target">{d.required_side_effect_target}</span>
        <span className="drow-age">{d.age_minutes}m ago</span>
      </div>
      <div className="drow-states">
        <span className="drow-expected">{d.expected_to_state}</span>
        <span className="drow-arrow">→</span>
        <span className="drow-actual">still {d.actual_state}</span>
      </div>
      <div className="drow-meta">
        <a
          className="drow-link"
          href={`#completion/${d.completion_section_id}`}
          aria-label={`Completion ${d.completion_section_id}`}
        >
          {d.completion_section_id}
        </a>
        {isUnverifiable && (
          <span className="drow-type-badge">v1 unverifiable</span>
        )}
      </div>
    </div>
  );
}

export default function PanelDiscrepancies({
  discrepancies,
}: {
  discrepancies: Discrepancy[];
}) {
  const [list, setList] = useState<Discrepancy[]>(discrepancies);

  useEffect(() => {
    // Auto-clear: update list on every poll result (N11 — no manual dismiss).
    setList(discrepancies);
  }, [discrepancies]);

  // API guarantees descending age_minutes order (N10). Render as-is.
  return (
    <section
      className={`panel panel-discrepancies ${list.length === 0 ? "is-empty" : "is-populated"}`}
      aria-labelledby="panel-discrepancies-title"
    >
      <header className="panel-head">
        <h2 className="panel-title" id="panel-discrepancies-title">
          Discrepancies
        </h2>
        <span className="panel-meta">
          <span className="panel-count">{list.length}</span>
          <span className="panel-sub">state-flip mismatches · polled every 15s</span>
        </span>
      </header>
      <div className="panel-body">
        {list.length === 0 && (
          <div className="panel-empty">No discrepancies detected.</div>
        )}
        {list.map((d) => (
          <DiscrepancyRow
            key={`${d.completion_section_id}:${d.required_side_effect_target}`}
            d={d}
          />
        ))}
      </div>
    </section>
  );
}
